import AddIcon from "@mui/icons-material/Add";
import { Alert, Button, CircularProgress, Stack } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { getFloatingNavigationScrollPaddingBottom } from "@src/appV2/redesign/components/FloatingNavigation/scrollPadding";
import { PullToRefresh } from "@src/appV2/redesign/components/PullToRefresh/PullToRefresh";
import { Text } from "@src/appV2/redesign/components/Text";
import { useHistory } from "react-router-dom";

import { type Case, CaseStatus } from "../types";
import { getHomeHealthCaseMobileDetailPath, HOME_HEALTH_CREATE_CASE_PATH } from "../paths";
import { MobileCaseCard } from "./MobileCaseCard";
import { MobileCaseStatusTabs } from "./MobileCaseStatusTabs";
import { MobileHomeHealthPageHeader } from "./MobileHomeHealthPageHeader";

const createButtonSx = {
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

interface Props {
  cases: Case[];
  status: CaseStatus;
  onChangeStatus: (status: CaseStatus) => void;
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => Promise<unknown> | unknown;
  onReopen: (caseItem: Case) => void;
}

export function MobileCasesListView({
  cases,
  status,
  onChangeStatus,
  isLoading,
  isError,
  onRefresh,
  onReopen,
}: Props) {
  const { tu } = useTu("homeHealth");
  const history = useHistory();
  const isActiveTab = status === CaseStatus.OPEN;

  function renderBody() {
    if (isLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Alert severity="error" sx={{ width: "100%" }}>
          {tu("Error loading cases.")}
        </Alert>
      );
    }

    if (cases.length === 0) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
          <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.disabled })}>
            {isActiveTab ? tu("No active cases") : tu("No closed cases")}
          </Text>
        </Stack>
      );
    }

    return (
      <Stack spacing={5}>
        {cases.map((caseItem) => (
          <MobileCaseCard
            key={caseItem.id}
            case={caseItem}
            onClick={() => {
              history.push(getHomeHealthCaseMobileDetailPath(caseItem.id));
            }}
            onReopen={() => {
              onReopen(caseItem);
            }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      sx={(theme) => ({
        // Fill (not exceed) the fixed-height, overflow-hidden page wrapper so only
        // the list scrolls; minHeight:"100vh" overflowed the clip and hid the last card.
        height: "100%",
        minHeight: 0,
        bgcolor: "background.page",
        padding: theme.spacing(3),
      })}
    >
      <Stack
        sx={(theme) => ({
          paddingX: theme.spacing(3),
          gap: theme.spacing(4),
        })}
      >
        <MobileHomeHealthPageHeader title={tu("Cases")} />
        <MobileCaseStatusTabs value={status} onChange={onChangeStatus} />
      </Stack>
      <PullToRefresh
        onRefresh={onRefresh}
        disabled={isLoading}
        sx={(theme) => ({
          paddingTop: theme.spacing(4),
          paddingX: theme.spacing(3),
          // Clear the floating nav / iOS home bar so the last card isn't hidden.
          paddingBottom: getFloatingNavigationScrollPaddingBottom(theme) ?? theme.spacing(4),
        })}
      >
        <Stack data-dd-privacy="mask" sx={(theme) => ({ gap: theme.spacing(4) })}>
          {/* Adding a case only makes sense from the Active tab (matches desktop). */}
          {isActiveTab && (
            <Button
              fullWidth
              variant="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                history.push(HOME_HEALTH_CREATE_CASE_PATH);
              }}
              sx={createButtonSx}
            >
              {tu("Add Case")}
            </Button>
          )}
          {renderBody()}
        </Stack>
      </PullToRefresh>
    </Stack>
  );
}
